import type { StudyToolKey } from '../../../study-metrics';
import type { StudyRingTone } from '../../../study-plan';
import type { IconComponent } from '../../components/Button';
import {
  AudioLines,
  ChartColumnIncreasing,
  ClipboardList,
  FlashcardIcon,
  ListChecks,
  WalletCards,
} from '../../icons';
import type { StudyToolTone } from './StudyToolCard.types';

/**
 * The four Study tools' IDENTITY — glyph, tile tone, and which Set-Mastery
 * metric their ring feeds. Read off Figma `67:20228`.
 *
 * One table, both platforms: a tool's colour and icon are the same fact on the
 * phone card and on web's hover card, and stating them twice is how the two
 * drift (they already disagreed on the flashcards Total glyph). What is NOT
 * here is anything platform- or surface-specific — the title comes from each
 * app's own i18n namespace, the CTA copy and press behaviour from the caller,
 * and the Create-jump target from whichever tab model that app has.
 */
export interface StudyToolSpec {
  key: StudyToolKey;
  icon: IconComponent;
  /** Leading glyph on the Total pill. */
  totalIcon: IconComponent;
  tone: StudyToolTone;
  /** Which Set-Mastery metric the ring feeds — Memory (primary) vs Applied. */
  ringTone: StudyRingTone;
}

export const STUDY_TOOL_SPECS: readonly StudyToolSpec[] = [
  {
    key: 'flashcards',
    icon: ChartColumnIncreasing,
    // Figma `67:20248` puts the flashcard glyph on this Total pill; the
    // practice tools use wallet-cards.
    totalIcon: FlashcardIcon,
    tone: 'primary',
    ringTone: 'primary',
  },
  {
    key: 'quizzes',
    icon: ListChecks,
    totalIcon: WalletCards,
    tone: 'success',
    ringTone: 'tertiary',
  },
  {
    key: 'oralExams',
    icon: AudioLines,
    totalIcon: WalletCards,
    tone: 'orange',
    ringTone: 'tertiary',
  },
  {
    key: 'caseStudies',
    icon: ClipboardList,
    totalIcon: WalletCards,
    tone: 'pink',
    ringTone: 'tertiary',
  },
];

/** Lookup by key, for callers that render in their own order. */
export const STUDY_TOOL_SPEC: Record<StudyToolKey, StudyToolSpec> =
  STUDY_TOOL_SPECS.reduce(
    (acc, spec) => {
      acc[spec.key] = spec;
      return acc;
    },
    {} as Record<StudyToolKey, StudyToolSpec>,
  );

/**
 * How many tool cards fit at a given container width.
 *
 * Web states this as `grid-cols-1 sm:grid-cols-2`; native has no media
 * queries, so the same threshold has to be a number it can compute from
 * `useWindowDimensions`. Both read it here, so the phone, the mobile-web pane
 * and the coming iPad two-pane agree on where the second column starts.
 */
export const STUDY_TOOL_TWO_COLUMN_MIN_WIDTH = 640;

export const studyToolColumns = (width: number): 1 | 2 =>
  width >= STUDY_TOOL_TWO_COLUMN_MIN_WIDTH ? 2 : 1;
